import type {
  ChecklistTemplateInput,
  ChecklistTemplateItem,
  ComplianceExtraStats,
  ComplianceIssue,
  ComplianceLog,
  ComplianceSummary,
  CreateIssueInput,
  CursorPage,
  ImportIssuesInput,
  ImportIssuesResult,
  IssueActionInput,
  QuetLaiResult,
  UpdateIssueInput,
} from '@qlk/shared';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';

import { api, goiApi, type LoiApi } from '../../lib/api.js';

const GOC = '/api/v1/compliance';

export interface BoLocSuCo {
  channelId?: string;
  type?: string;
  status?: string;
  onlyOpen?: boolean;
}

export const KHOA = {
  danhSach: (loc: BoLocSuCo) => ['compliance', 'issues', loc] as const,
  tomTat: ['compliance', 'summary'] as const,
  thongKe: ['compliance', 'thong-ke'] as const,
  checklist: ['compliance', 'checklist'] as const,
  nhatKy: (issueId?: string) => ['compliance', 'logs', issueId ?? 'tat-ca'] as const,
};

function thamSo(loc: BoLocSuCo, cursor?: string): string {
  const sp = new URLSearchParams();
  for (const [khoa, giaTri] of Object.entries(loc)) {
    if (giaTri !== undefined && giaTri !== '') sp.set(khoa, String(giaTri));
  }
  if (cursor) sp.set('cursor', cursor);
  const chuoi = sp.toString();
  return chuoi ? `?${chuoi}` : '';
}

type TrangSuCo = CursorPage<ComplianceIssue>;

export function useDanhSachSuCo(loc: BoLocSuCo) {
  return useInfiniteQuery<
    TrangSuCo,
    LoiApi,
    InfiniteData<TrangSuCo>,
    ReturnType<typeof KHOA.danhSach>,
    string | undefined
  >({
    queryKey: KHOA.danhSach(loc),
    queryFn: ({ pageParam }) => api.get<TrangSuCo>(`${GOC}/issues${thamSo(loc, pageParam)}`),
    initialPageParam: undefined,
    getNextPageParam: (trang) => trang.nextCursor ?? undefined,
  });
}

export function useTomTatTuanThu() {
  return useQuery<ComplianceSummary, LoiApi>({
    queryKey: KHOA.tomTat,
    queryFn: () => api.get<ComplianceSummary>(`${GOC}/summary`),
  });
}

export function useThongKeTuanThu() {
  return useQuery<ComplianceExtraStats, LoiApi>({
    queryKey: KHOA.thongKe,
    queryFn: () => api.get<ComplianceExtraStats>(`${GOC}/thong-ke`),
  });
}

export function useChecklist() {
  return useQuery<ChecklistTemplateItem[], LoiApi>({
    queryKey: KHOA.checklist,
    queryFn: () => api.get<ChecklistTemplateItem[]>(`${GOC}/checklist`),
  });
}

export function useNhatKy(issueId?: string) {
  return useQuery<ComplianceLog[], LoiApi>({
    queryKey: KHOA.nhatKy(issueId),
    queryFn: () =>
      api.get<ComplianceLog[]>(`${GOC}/nhat-ky${issueId ? `?issueId=${issueId}` : ''}`),
  });
}

/* ------------------------------- Mutation ------------------------------ */

function lamMoiTatCa(qc: ReturnType<typeof useQueryClient>) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ['compliance', 'issues'] }),
    qc.invalidateQueries({ queryKey: KHOA.tomTat }),
    qc.invalidateQueries({ queryKey: KHOA.thongKe }),
    qc.invalidateQueries({ queryKey: ['compliance', 'logs'] }),
  ]);
}

export function useTaoSuCo() {
  const qc = useQueryClient();
  return useMutation<ComplianceIssue, LoiApi, CreateIssueInput>({
    mutationFn: (input) => api.post<ComplianceIssue>(`${GOC}/issues`, input),
    onSuccess: () => lamMoiTatCa(qc),
  });
}

export function useCapNhatSuCo() {
  const qc = useQueryClient();
  return useMutation<ComplianceIssue, LoiApi, { id: string; input: UpdateIssueInput }>({
    mutationFn: ({ id, input }) =>
      goiApi<ComplianceIssue>(`${GOC}/issues/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: () => lamMoiTatCa(qc),
  });
}

export function useHanhDongSuCo() {
  const qc = useQueryClient();
  return useMutation<ComplianceIssue, LoiApi, { id: string; input: IssueActionInput }>({
    mutationFn: ({ id, input }) =>
      api.post<ComplianceIssue>(`${GOC}/issues/${id}/hanh-dong`, input),
    onSuccess: () => lamMoiTatCa(qc),
  });
}

export function useThemNhatKy() {
  const qc = useQueryClient();
  return useMutation<ComplianceLog, LoiApi, { id: string; text: string }>({
    mutationFn: ({ id, text }) => api.post<ComplianceLog>(`${GOC}/issues/${id}/log`, { text }),
    onSuccess: () => lamMoiTatCa(qc),
  });
}

export function useLuuChecklist() {
  const qc = useQueryClient();
  return useMutation<ChecklistTemplateItem, LoiApi, { key: string; input: ChecklistTemplateInput }>(
    {
      mutationFn: ({ key, input }) =>
        goiApi<ChecklistTemplateItem>(`${GOC}/checklist/${key}`, {
          method: 'PUT',
          body: JSON.stringify(input),
        }),
      onSuccess: () => qc.invalidateQueries({ queryKey: KHOA.checklist }),
    },
  );
}

export function useQuetLai() {
  const qc = useQueryClient();
  return useMutation<QuetLaiResult, LoiApi, void>({
    mutationFn: () => api.post<QuetLaiResult>(`${GOC}/quet-lai`),
    onSuccess: () => lamMoiTatCa(qc),
  });
}

export function useNhapSuCoCsv() {
  const qc = useQueryClient();
  return useMutation<ImportIssuesResult, LoiApi, ImportIssuesInput>({
    mutationFn: (input) => api.post<ImportIssuesResult>(`${GOC}/import`, input),
    onSuccess: () => lamMoiTatCa(qc),
  });
}

/** Kênh cho ô chọn của form thêm sự cố — endpoint riêng của module 09. */
export function useKenhChonSuCo() {
  return useQuery<{ id: string; name: string; handle: string }[], LoiApi>({
    queryKey: ['compliance', 'kenh'],
    queryFn: () => api.get<{ id: string; name: string; handle: string }[]>(`${GOC}/kenh`),
  });
}
